import { IconShieldLock } from "@tabler/icons-react";
import type { FC } from "react";
import { Dialog, Modal, ModalOverlay } from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { Button, ButtonVariant } from "../Buttons/button.js";
import { Size } from "../helpers/helpers.js";

const LOGO_SRC = "https://assets.alpharun.com/alpharun_white.png";

export interface PrivacyCurtainProps {
  /** The curtain's accessible name, e.g. "HQ hidden". Never shown. */
  label?: string;
  /** Overrides the reveal button label, e.g. "Show HQ". */
  buttonLabel?: string;
  /** Class overrides for the curtain surface, e.g. a different background. */
  className?: string;
  onReveal: () => void;
}

/**
 * Fully opaque full-viewport cover shown after the tab has been backgrounded
 * for a while, so switching to an Alpharun tab while screen sharing never
 * exposes the underlying page. Painted as a solid background — deliberately
 * not a blur of the page underneath.
 *
 * It is a React Aria modal so it behaves like one on top of whatever is
 * already open: it joins the overlay stack as the topmost layer, makes the
 * rest of the document inert (any open modal or slideover included), contains
 * focus, and cannot be dismissed by Escape or by clicking through — only the
 * reveal button lifts it. The z-index is still ours to set, since inertness
 * says nothing about paint order.
 */
export const PrivacyCurtain: FC<PrivacyCurtainProps> = (props) => {
  const { label, buttonLabel, className, onReveal } = props;

  return (
    <ModalOverlay
      isOpen
      isDismissable={false}
      isKeyboardDismissDisabled
      // Deliberately not the shared `modalOverlay()` styles: those size the
      // overlay to the visual viewport, which leaves the page showing below a
      // mobile keyboard.
      className={twMerge("fixed inset-0 z-[9999] bg-slate-950", className)}
    >
      <Modal className="h-full w-full outline-none">
        <Dialog
          aria-label={label ?? "Workspace hidden"}
          className="flex h-full w-full flex-col items-center justify-center gap-3 px-6 text-center text-white outline-none"
        >
          <img src={LOGO_SRC} alt="Alpharun" className="mb-6 h-8" />
          <IconShieldLock aria-hidden size={48} stroke={1.5} />
          <Button
            variant={ButtonVariant.Secondary}
            size={Size.LARGE}
            className="mt-3"
            onClick={onReveal}
          >
            {buttonLabel ?? "Show workspace"}
          </Button>
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
};
